<template>
  <v-dialog :model-value="isDialogActived" max-width="1180px" persistent scrollable>
    <v-card class="analysis-dialog">
      <v-card-title class="dialog-title">
        <span>Detalles de Analisis</span>
        <v-spacer></v-spacer>
        <v-chip color="primary" variant="tonal">Caida: {{ caidaTotal }}%</v-chip>
        <v-chip color="secondary" variant="tonal">Defectos: {{ defectosTotal }}%</v-chip>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="saveError" type="error" variant="tonal" class="mb-4">{{ saveError }}</v-alert>
        <v-form ref="form" v-model="valid" validate-on="lazy">
          <section class="form-section">
            <h2>Zarandas utilizadas</h2>
            <v-row v-for="(sieve, index) in editedItem.zarandas" :key="index" dense align="center">
              <v-col cols="12" sm="5">
                <number-stepper v-model="sieve.zaranda" :label="`Zaranda ${index + 1}`" type="number" min="0" max="100" step="0.1"
                  :rules="[rules.requiredFieldRule, rules.min0, rules.max100]" v-bind="fieldProps" />
              </v-col>
              <v-col cols="10" sm="5">
                <number-stepper v-model="sieve.caida" :label="`Caída zaranda ${index + 1}`" suffix="%" type="number" min="0" max="100" step="0.1"
                  :rules="[rules.requiredFieldRule, rules.min0, rules.max100]" v-bind="fieldProps" />
              </v-col>
              <v-col cols="2">
                <v-btn v-if="canSave && editedItem.zarandas.length > 1" icon="delete" variant="text" color="error" min-height="48"
                  :disabled="saving" :aria-label="`Quitar zaranda ${index + 1}`" @click="editedItem.zarandas.splice(index, 1)" />
              </v-col>
            </v-row>
            <v-btn v-if="canSave" variant="tonal" prepend-icon="add" min-height="48" :disabled="saving"
              @click="editedItem.zarandas.push({ zaranda: null, caida: null })">Agregar zaranda</v-btn>
            <p class="mt-3">Caída total de zarandas: {{ totalSieveFall }}%</p>
          </section>
          <section class="form-section">
            <h2>Datos generales</h2>
            <v-row dense>
              <v-col cols="12" sm="6" md="3"><GramajeField v-model="editedItem.gramaje" :readonly="!canSave || saving" /></v-col>
              <v-col v-for="field in generalFields" :key="field.key" cols="12" sm="6" md="3">
                <number-stepper
                  v-model="editedItem[field.key]"
                  :label="field.label"
                  :suffix="field.suffix"
                  :rules="field.rules"
                  type="number"
                  min="0"
                  :max="field.max"
                  :step="field.step || '0.1'"
                  v-bind="fieldProps"
                />
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <h2>Caida</h2>
            <v-row dense>
              <v-col v-for="field in caidaFields" :key="field.key" cols="12" sm="6" md="3">
                <number-stepper
                  v-model="editedItem[field.key]"
                  :label="field.label"
                  suffix="%"
                  :rules="field.rules"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  v-bind="fieldProps"
                />
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <h2>Defectos</h2>
            <v-row dense>
              <v-col v-for="field in defectoFields" :key="field.key" cols="12" sm="6" md="3">
                <number-stepper
                  v-model="editedItem[field.key]"
                  :label="field.label"
                  suffix="%"
                  :rules="field.rules"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  v-bind="fieldProps"
                />
              </v-col>
            </v-row>
          </section>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="secondary" variant="text" min-height="48" :disabled="saving" @click="close(true)">Cancelar</v-btn>
        <v-btn v-if="canSave" color="primary" min-height="48" :loading="saving" @click="save">Guardar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from 'vuex'
import { getCaidaTotal, getDefectosTotal } from '../../services/analysis/AnalysisUtilFuntions'
import { todayLocalDate } from '../../services/common/UtilsFunctions'
import NumberStepper from '../Common/NumberStepper'
import GramajeField from '../Common/GramajeField.vue'
import { analysisSieves, sieveFall } from '../../services/analysis/AnalysisSieves'

export default {
  name: 'AddEditReadAnalysisForm',
  components: {
    NumberStepper,
    GramajeField
  },
  props: {
    analysisTitle: String
  },
  computed: {
    ...mapGetters('analysisModule', ['isDialogActived']),
    ...mapGetters('authModule', ['hasPermission']),
    canSave () { return this.hasPermission(this.editedItem.id > 0 ? 'plant.analyses.update' : 'plant.analyses.create') },
    fieldProps () { return { variant: 'outlined', density: 'comfortable', readonly: !this.canSave || this.saving } },
    totalSieveFall () { return sieveFall(this.editedItem.zarandas) },
    caidaTotal () {
      return getCaidaTotal({ ...this.editedItem, caidaZaranda: this.totalSieveFall })
    },
    defectosTotal () {
      return getDefectosTotal(this.editedItem)
    },
    generalFields () {
      return [
        { key: 'humedad', label: 'Humedad', suffix: '%', max: 100, rules: [this.rules.min0, this.rules.max100] },
        { key: 'hidratacion', label: 'Hidratacion', suffix: '%', max: 100, rules: [this.rules.min0, this.rules.max100] },
        { key: 'tiempoCoccion', label: 'Tiempo de Coccion', suffix: 'hora/s', max: 100, rules: [this.rules.min0, this.rules.max100] }
      ]
    },
    caidaFields () {
      return [
        { key: 'incomestible', label: 'Incomestible', rules: [this.rules.min0, this.rules.max100] },
        { key: 'terron', label: 'Terron', rules: [this.rules.min0, this.rules.max100] },
        { key: 'manchado', label: 'Manchado', rules: [this.rules.min0, this.rules.max100] },
        { key: 'partido', label: 'Partido', rules: [this.rules.min0, this.rules.max100] },
        { key: 'helado', label: 'Helado', rules: [this.rules.min0, this.rules.max100] },
        { key: 'chuzo', label: 'Chuzo', rules: [this.rules.min0, this.rules.max100] },
        { key: 'roido', label: 'Roido', rules: [this.rules.min0, this.rules.max100] },
        { key: 'picadoCampo', label: 'Picado de Campo', rules: [this.rules.min0, this.rules.max100] },
        { key: 'contrastante', label: 'Contrastante', rules: [this.rules.min0, this.rules.max100] },
        { key: 'descorticado', label: 'Descorticado', rules: [this.rules.min0, this.rules.max100] },
        { key: 'quebrado', label: 'Quebrado', rules: [this.rules.min0, this.rules.max100] },
        { key: 'brotado', label: 'Brotado', rules: [this.rules.min0, this.rules.max100] },
        { key: 'revolcado', label: 'Revolcado', rules: [this.rules.min0, this.rules.max100] },
        { key: 'leveA', label: 'Leve A', rules: [this.rules.min0, this.rules.max100] }
      ]
    },
    defectoFields () {
      return [
        { key: 'leveB', label: 'Leve B', rules: [this.rules.min0, this.rules.max100] },
        { key: 'cascado', label: 'Cascado', rules: [this.rules.min0, this.rules.max100] },
        { key: 'manchadoMaquina', label: 'Manchado de Maquina', rules: [this.rules.min0, this.rules.max100] },
        { key: 'pinto', label: 'Pinto', rules: [this.rules.min0, this.rules.max100] },
        { key: 'bolita', label: 'Bolita', rules: [this.rules.min0, this.rules.max100] },
        { key: 'oxidado', label: 'Oxidado', rules: [this.rules.min0, this.rules.max100] },
        { key: 'puntaChaucha', label: 'Punta de Chaucha', rules: [this.rules.min0, this.rules.max100] },
        { key: 'arrugado', label: 'Arrugado', rules: [this.rules.min0, this.rules.max100] },
        { key: 'venoso', label: 'Venoso', rules: [this.rules.min0, this.rules.max100] },
        { key: 'decolorido', label: 'Decolorido', rules: [this.rules.min0, this.rules.max100] }
      ]
    }
  },
  watch: {
    'isDialogActived': function (val) {
      if (val) {
        const currentAnalysis = this.$store.getters['analysisModule/getAnalysis']
        const defaultItem = this.$store.getters['analysisModule/getDefaultItem']
        this.editedItem = Object.assign({}, defaultItem, currentAnalysis)
        this.editedItem.zarandas = analysisSieves(this.editedItem)
        this.saveError = ''
      }
    }
  },

  methods: {
    resetIncomeForm () {
      if (!this.$store.getters['incomeModule/isDialogActived']) {
        this.$store.dispatch('incomeModule/setSelectedItem', this.$store.getters['incomeModule/getDefaultItem'])
      }
    },

    close (resetData) {
      let defaultItem = this.$store.getters['analysisModule/getDefaultItem']
      this.editedItem = Object.assign({}, defaultItem)
      this.valid = true
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
      if (resetData && !this.$store.getters['incomeModule/isDialogActived']) {
        this.$store.dispatch('analysisModule/setAnalysisData', Object.assign({}, defaultItem))
      }
      this.resetIncomeForm()
      this.$store.dispatch('analysisModule/setDialogActived', false)
    },

    async save () {
      if (this.saving || !this.canSave) return
      this.saving = true
      this.saveError = ''
      try {
        const validation = await this.$refs.form.validate()
        if (!validation.valid) return
        if (this.totalSieveFall > 100) {
          this.saveError = 'La caída total de zarandas no puede superar el 100%.'
          return
        }
        const sizes = this.editedItem.zarandas.map(item => Number(item.zaranda))
        if (new Set(sizes).size !== sizes.length) {
          this.saveError = 'Indique cada zaranda una sola vez.'
          return
        }
        this.editedItem.zaranda = Number(this.editedItem.zarandas[0].zaranda)
        this.editedItem.caidaZaranda = this.totalSieveFall
        const income = this.$store.getters['incomeModule/getSelectedItem']
        if (this.$store.getters['incomeModule/isDialogActived'] || !(income.id > 0)) {
          this.$store.dispatch('analysisModule/setAnalysisData', Object.assign({}, this.editedItem))
          this.close(false)
        } else {
          const item = Object.assign({}, this.editedItem, { incomeId: income.id })
          await this.$store.dispatch('analysisModule/saveAnalysis', item)
          this.close(true)
        }
      } catch (e) {
        this.saveError = e.response?.data?.message || 'No se pudo confirmar el guardado. Revise la entrada antes de reintentar.'
      } finally {
        this.saving = false
      }
    }
  },
  data: () => ({
    valid: true,
    saving: false,
    saveError: '',
    rules: {
      requiredFieldRule: v => (v !== null && v !== undefined && v !== '') || 'Campo requerido.',
      min0: v => (v === null || v === '' || Number(v) >= 0) || 'El valor no puede ser menor que 0',
      max100: v => !(v > 100) || 'El valor no puede ser mayor que 100'
    },
    editedItem: {
      id: -1,
      zaranda: null,
      zarandas: [],
      caidaZaranda: null,
      incomestible: null,
      terron: null,
      manchado: null,
      partido: null,
      helado: null,
      chuzo: null,
      tiempoCoccion: null,
      roido: null,
      picadoCampo: null,
      contrastante: null,
      leveA: null,
      leveB: null,
      descorticado: null,
      cascado: null,
      arrugado: null,
      revolcado: null,
      manchadoMaquina: null,
      puntaChaucha: null,
      pinto: null,
      bolita: null,
      oxidado: null,
      quebrado: null,
      brotado: null,
      venoso: null,
      decolorido: null,
      humedad: null,
      gramaje: null,
      hidratacion: null,
      analysisDate: todayLocalDate()
    }
  })
}
</script>
<style scoped>
.analysis-dialog {
  max-height: 90vh;
}

.dialog-title {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.form-section {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-bottom: 18px;
  padding-bottom: 10px;
}

.form-section:last-child {
  border-bottom: 0;
  margin-bottom: 0;
}

.form-section h2 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 12px;
}
</style>
